import * as ipaddr from 'ipaddr.js';
import { cache } from 'react';
import Moment from 'moment';


export function getIPAddress(req: Request) {
    const forwardedFor = req.headers.get('x-forwarded-for');
    if (forwardedFor) {
        return forwardedFor.split(',')[0];
    }
    if (req.headers.get('x-real-ip')) {
        return req.headers.get('x-real-ip');
    }
    if (req.headers.get('cf-connecting-ip')) {
        return req.headers.get('cf-connecting-ip');
    }
    if (req.headers.get('x-client-ip')) {
        return req.headers.get('x-client-ip');
    }
    if (req.headers.get('x-cluster-client-ip')) {
        return req.headers.get('x-cluster-client-ip');
    }
    if (req.headers.get('x-forwarded-host')) {
        return req.headers.get('x-forwarded-host');
    }

    return 'unknown';
}

export function getIPv4Address(ip: string) {
    let remoteAddress = ip;
    if (remoteAddress === '::1') {
        return '127.0.0.1';
    }

    if (ipaddr.isValid(remoteAddress)) {
        const addr = ipaddr.parse(remoteAddress);
        // @ts-expect-error: isIPv4MappedAddress is not recognized by TypeScript but is valid
        if (addr.kind() === 'ipv6' && addr.isIPv4MappedAddress()) {
            // @ts-expect-error: toIPv4Address is not recognized by TypeScript but is valid
            remoteAddress = addr.toIPv4Address().toString();
        }
    }
    return remoteAddress;
}


export function isNumeric(val: number | string | string[] | undefined): boolean {
    if (typeof val == 'number') {
        return !isNaN(val);
    }

    if (typeof val === 'string' && val.trim() !== '') {
        return !isNaN(Number(val));
    }

    return false;
}

export function getRandomNumber(min: number, max: number): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1) + min);
}

export function secondsToHms(second: number) {
    const h = Math.floor(second / 3600);
    second %= 3600;
    const m = Math.floor(second / 60);
    const s = second % 60;
    return { h, m, s };
}

export function secondsToHmsFormatted(second: number) {
    const { h, m, s } = secondsToHms(second);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function secondsToHmsVerbose(second: number, hourString = 'hour', minuteString = 'minute', secondString = 'second', dayString = 'day'): string {
    const { h, m, s } = secondsToHms(second);
    if (h >= 24) {
        const days = Math.floor(h / 24);
        const remHours = h % 24;
        return `${days} ${dayString}${days > 1 ? 's' : ''} ${remHours > 0 ? `${remHours} ${hourString}${remHours > 1 ? 's' : ''} ` : ''}${m > 0 || s > 0 ? `${m} ${minuteString}${m > 1 ? 's' : ''} ` : ''}${s > 0 ? `${s} ${secondString}${s > 1 ? 's' : ''}` : ''}`;
    }
    return `${h > 0 ? `${h} ${hourString}${h > 1 ? 's' : ''} ` : ''}${m > 0 || s > 0 ? `${m} ${minuteString}${m > 1 ? 's' : ''} ` : ''}${s > 0 ? `${s} ${secondString}${s > 1 ? 's' : ''}` : ''}`;
}

export function getMinuteSecondString(milli: number) {
    const { h, m, s } = secondsToHms(Math.floor(milli / 1000));
    return `${String(h * 60 + m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export async function isAppInstalled(appManifestUrl: string): Promise<boolean> {
    if ('getInstalledRelatedApps' in navigator) {
        const relatedApps = await (navigator as any).getInstalledRelatedApps();
        return relatedApps.some((app: any) => app.url === appManifestUrl);
    }
    return false;
};

export function getSecondsFromTime(timeStr: string): number {
    const parts = timeStr.split(':').reverse();
    let totalSeconds = 0;
    totalSeconds += parseInt(parts[0] || '0', 10);

    if (parts.length > 1) {
        totalSeconds += parseInt(parts[1], 10) * 60;
    }

    if (parts.length > 2) {
        totalSeconds += parseInt(parts[2], 10) * 3600;
    }

    return totalSeconds;
}

export const fetchDataWithCache = cache(
    async (url: string, headers?: Record<string, string>, revalidate: number = 3600, timeout: number = 30): Promise<any> => {
        try {
            const response = await fetch(url, {
                headers,
                next: { revalidate },
                signal: AbortSignal.timeout(timeout * 1000),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch: ${response.statusText}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Fetch Error:", error);
            return null;
        }
    }
);

export function displayDate(date: Date | string | null, displayOnlyYear: boolean, hideDate: boolean) {

    if (!date) return null;

    if (displayOnlyYear) {
        return Moment(date).format('Y');
    }

    if (hideDate) {
        return Moment(date).format('MMMM Y');
    }

    return Moment(date).format('MMMM D, Y');
}